import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { mobileApi } from '../../services/api';
import { enqueueInspection } from '../../services/inspectionQueue';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import SignaturePad from 'signature_pad';
import { Camera, Loader2, MapPin, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const MobileInspectionFlow = () => {
  const { assetId, templateId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useMobileAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [asset, setAsset] = useState(null);
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [expanded, setExpanded] = useState({}); // item id -> note area open
  const [finish, setFinish] = useState(false);
  const [location, setLocation] = useState(null);

  const canvasRef = useRef(null);
  const padRef = useRef(null);

  useEffect(() => { loadData(); captureLocation(); /* eslint-disable-next-line */ }, []);

  const loadData = async () => {
    try {
      const [a, t] = await Promise.all([
        mobileApi.getAsset(assetId, getToken()),
        mobileApi.getInspectionTemplate(templateId, getToken()),
      ]);
      setAsset(a.data); setTemplate(t.data);
      const init = {};
      (t.data.checklist || []).forEach((it) => { init[it.id] = { result: null, comment: '', photo: null }; });
      setResponses(init);
    } catch { toast.error('Failed to load inspection'); navigate('/mobile/inspections'); }
    finally { setLoading(false); }
  };

  const captureLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setLocation({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy, captured_at: new Date().toISOString() }),
      () => {}, { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (finish && canvasRef.current && !padRef.current) {
      const c = canvasRef.current;
      c.width = c.offsetWidth * 2; c.height = c.offsetHeight * 2; c.getContext('2d').scale(2, 2);
      padRef.current = new SignaturePad(c, { backgroundColor: 'rgb(255,255,255)', penColor: 'rgb(26,26,26)' });
    }
    if (!finish) padRef.current = null;
  }, [finish]);

  const checklist = template?.checklist || [];
  const setResult = (id, result) => setResponses((p) => ({ ...p, [id]: { ...p[id], result } }));
  const setComment = (id, comment) => setResponses((p) => ({ ...p, [id]: { ...p[id], comment } }));
  const setPhoto = (id, photo) => setResponses((p) => ({ ...p, [id]: { ...p[id], photo } }));

  const answered = checklist.filter((it) => responses[it.id]?.result != null).length;
  const fails = checklist.filter((it) => responses[it.id]?.result === 'fail').length;
  const total = checklist.length;
  const firstUnansweredIdx = checklist.findIndex((it) => responses[it.id]?.result == null);
  const allAnswered = firstUnansweredIdx === -1 && total > 0;

  const assetId2 = asset?.unit_number || asset?.name || 'this asset';

  const photoInput = (id) => {
    const el = document.getElementById(`photo-${id}`);
    el?.click();
  };
  const onPhoto = (id, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = (ev) => setPhoto(id, ev.target.result); r.readAsDataURL(f);
    e.target.value = '';
  };

  const doSubmit = async () => {
    if (padRef.current?.isEmpty()) { toast.error('Please add your signature'); return; }
    const formatted = checklist.map((it) => ({
      item_id: it.id, result: responses[it.id]?.result || 'na',
      comment: responses[it.id]?.comment || null, photo: responses[it.id]?.photo || null,
    }));
    const payload = { asset_id: assetId, template_id: templateId, responses: formatted, signature: padRef.current.toDataURL('image/png'), location };
    if (!navigator.onLine) {
      enqueueInspection(payload);
      toast.success('Saved offline — it will send when you have signal');
      navigate('/mobile/inspections'); return;
    }
    setSubmitting(true);
    try {
      await mobileApi.submitInspection(payload, getToken());
      toast.success(fails > 0 ? `Inspection submitted · ${fails} defect${fails === 1 ? '' : 's'} raised` : 'Inspection passed');
      navigate('/mobile/inspections');
    } catch (e) {
      if (!e.response) { enqueueInspection(payload); toast.success('Saved offline — it will send when you have signal'); navigate('/mobile/inspections'); }
      else toast.error('Failed to submit inspection');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[var(--surface-page)] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[var(--volt-icon)] animate-spin" /></div>;

  // ── 5c FINISH ──────────────────────────────────────────────────────────
  if (finish) {
    const passed = checklist.filter((it) => responses[it.id]?.result === 'pass').length;
    const na = checklist.filter((it) => responses[it.id]?.result === 'na').length;
    const failedItems = checklist.filter((it) => responses[it.id]?.result === 'fail');
    const ready = !padRef.current?.isEmpty?.() ? true : false; // recomputed on render via sigTick
    const offline = typeof navigator !== 'undefined' && !navigator.onLine;
    return (
      <div className="min-h-screen bg-[var(--surface-page)] pb-40" data-testid="insp-finish">
        <MobileHeader title="Finish inspection" showBack onBack={() => setFinish(false)} />
        <div className="px-4 py-4 space-y-4">
          {/* result card */}
          <MobileCard spine={fails > 0 ? 'var(--overdue)' : 'var(--pass)'} padding="normal">
            <div className="text-[16px] font-extrabold" style={{ color: fails > 0 ? 'var(--critical-ink)' : 'var(--pass-ink)' }}>
              {fails > 0 ? 'Failed' : 'Passed'}
              <span className="font-medium text-[13px] text-[var(--ink-secondary)]"> — {fails > 0 ? `${fails} item${fails === 1 ? '' : 's'} need${fails === 1 ? 's' : ''} action` : 'all clear'}</span>
            </div>
            <div className="font-mono text-[14px] font-bold text-[var(--ink-primary)] mt-1">{assetId2} <span className="font-sans font-medium text-[var(--ink-secondary)]">· {template?.name}</span></div>
            <div className="flex items-center gap-4 mt-2 text-[13px]">
              <span className="flex items-center gap-1.5"><span className="rounded-full" style={{ width: 7, height: 7, background: 'var(--pass)' }} /><span className="text-[var(--ink-secondary)]">{passed} passed</span></span>
              <span className="flex items-center gap-1.5"><span className="rounded-full" style={{ width: 7, height: 7, background: 'var(--overdue)' }} /><span className="text-[var(--ink-secondary)]">{fails} failed</span></span>
              <span className="flex items-center gap-1.5"><span className="rounded-full" style={{ width: 7, height: 7, background: '#8A857A' }} /><span className="text-[var(--ink-secondary)]">{na} N/A</span></span>
            </div>
          </MobileCard>

          {/* failed items */}
          {failedItems.length > 0 && (
            <MobileCard padding="none">
              <div className="px-4 bg-[var(--surface-sunken)] rounded-t-[13px] flex items-center" style={{ height: 42 }}>
                <span className="text-[11px] font-bold uppercase tracking-[0.11em] text-[var(--ink-tertiary)]">Will be raised as a defect</span>
              </div>
              <div className="px-4">
                {failedItems.map((it, i) => {
                  const r = responses[it.id];
                  return (
                    <div key={it.id} className={`flex items-start justify-between gap-2 py-3 ${i ? 'border-t border-[var(--divider)]' : ''}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="shrink-0 rounded-full mt-0.5" style={{ width: 7, height: 7, background: 'var(--overdue)' }} />
                          <span className="text-[14px] font-semibold text-[var(--ink-primary)]">Item {checklist.indexOf(it) + 1} — {it.question}</span>
                        </div>
                        <div className="text-[12.5px] text-[var(--ink-tertiary)] mt-0.5 ml-4 truncate">
                          {r.comment ? `"${r.comment}"` : 'No note'}{r.photo ? ' · 1 photo' : ''}
                        </div>
                      </div>
                      <button onClick={() => { setFinish(false); setExpanded((p) => ({ ...p, [it.id]: true })); setTimeout(() => document.getElementById(`item-${it.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100); }}
                        className="text-[13px] font-semibold text-[var(--volt-ink)] shrink-0">Edit</button>
                    </div>
                  );
                })}
              </div>
            </MobileCard>
          )}

          {/* location */}
          {location && (
            <div className="w-full bg-white rounded-[13px] px-4 py-2.5 flex items-center justify-between" style={{ border: '1px solid var(--border)' }}>
              <div className="flex items-center gap-2 min-w-0">
                <MapPin className="w-4 h-4 text-[var(--volt-icon)] shrink-0" />
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold text-[var(--ink-primary)] truncate">{asset?.location || 'Current location'}</div>
                  <div className="font-mono text-[10.5px] text-[var(--ink-tertiary)]">±{Math.round(location.accuracy || 0)} m · captured {new Date(location.captured_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
              <button onClick={captureLocation} className="text-[13px] font-semibold text-[var(--volt-ink)] shrink-0">Redo</button>
            </div>
          )}

          {/* signature */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[13px] font-semibold text-[var(--ink-secondary)]">Signature</label>
              <button onClick={() => { padRef.current?.clear(); setSubmitting((s) => s); }} className="text-[13px] font-semibold text-[var(--volt-ink)]">Clear</button>
            </div>
            <div className="relative rounded-[12px] overflow-hidden bg-white" style={{ height: 112, border: '1.5px solid #C4BFB3' }}>
              <canvas ref={canvasRef} className="w-full h-full touch-none" style={{ touchAction: 'none' }} data-testid="insp-signature-pad" />
              <span className="absolute bottom-1.5 left-3 font-mono text-[10.5px] text-[var(--ink-tertiary)] pointer-events-none">A. Roche · {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pt-2.5 pb-4 bg-[var(--surface-page)]" style={{ boxShadow: 'var(--elev-floating)' }}>
          <p className="text-[12.5px] text-[var(--ink-secondary)] leading-snug mb-2.5" data-testid="insp-finish-consequence">
            Records the inspection against <b className="text-[var(--ink-primary)]">{assetId2}</b>{fails > 0 ? <> and raises <b className="text-[var(--ink-primary)]">{fails} defect{fails === 1 ? '' : 's'}</b> for the failed {fails === 1 ? 'item' : 'items'}.</> : '.'}
            {offline && <> <b className="text-[var(--ink-primary)]">You are offline</b> — saved here and sent when signal returns.</>}
          </p>
          <button onClick={doSubmit} disabled={submitting} data-testid="insp-submit-btn"
            className="w-full rounded-[13px] bg-[var(--volt)] text-black flex items-center justify-center active:scale-[0.99] transition-transform"
            style={{ height: 58, fontWeight: 800, border: '1px solid var(--volt-edge)' }}>
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'SUBMIT INSPECTION'}
          </button>
        </div>
      </div>
    );
  }

  // ── 5b RUN (scrolling checklist) ─────────────────────────────────────────
  const pct = total ? Math.round((answered / total) * 100) : 0;
  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-40" data-testid="insp-run">
      <MobileHeader showBack onBack={() => navigate('/mobile/inspections')}
        title={<span className="flex flex-col leading-tight">
          <span className="font-mono text-[10.5px] font-normal text-[var(--ink-tertiary)]">{assetId2}</span>
          <span className="text-[15.5px] font-bold text-[var(--ink-primary)] truncate">{template?.name}</span>
        </span>} />

      {/* sticky progress */}
      <div className="sticky top-[57px] z-30 bg-[var(--surface-card)] border-b border-[var(--border)] px-4 py-2.5">
        <div className="flex items-center justify-between text-[13px] mb-1.5">
          <span className="font-semibold text-[var(--ink-primary)]" data-testid="insp-progress">{answered} of {total} answered</span>
          {fails > 0 && <span className="font-semibold text-[var(--overdue-ink)]">{fails} fail</span>}
        </div>
        <div className="h-1.5 rounded-full overflow-hidden bg-[var(--surface-sunken)] flex">
          <div className="h-full bg-[var(--pass)]" style={{ width: `${((answered - fails) / total) * 100}%` }} />
          <div className="h-full bg-[var(--overdue)]" style={{ width: `${(fails / total) * 100}%` }} />
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {checklist.map((it, idx) => {
          const r = responses[it.id] || {};
          const isFail = r.result === 'fail';
          const showNote = isFail || expanded[it.id];
          const opts = [
            { k: 'pass', l: 'Pass', selCls: 'bg-[var(--pass-bg)] border-[var(--pass-edge)] text-[var(--pass-ink)]' },
            { k: 'fail', l: 'Fail', selCls: 'bg-[var(--critical-bg)] border-[var(--critical-edge)] text-[var(--critical-ink)]' },
            { k: 'na', l: 'N/A', selCls: 'bg-[var(--neutral-bg)] border-[var(--border-strong)] text-[var(--ink-secondary)]' },
          ];
          return (
            <MobileCard key={it.id} padding="normal" spine={isFail ? 'var(--overdue)' : undefined} id={`item-${it.id}`}>
              <div id={`item-${it.id}`} />
              <div className="flex gap-2 mb-3">
                <span className="font-mono text-[12px] font-bold text-[var(--ink-tertiary)] mt-0.5">{String(idx + 1).padStart(2, '0')}</span>
                <h2 className="text-[15px] font-semibold text-[var(--ink-primary)] leading-snug">{it.question}</h2>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {opts.map((o) => {
                  const sel = r.result === o.k;
                  return (
                    <button key={o.k} onClick={() => setResult(it.id, o.k)} data-testid={`insp-${idx}-${o.k}`}
                      className={`h-12 rounded-[10px] border text-[14px] font-semibold transition-colors ${sel ? o.selCls : 'bg-white text-[var(--ink-primary)] border-[var(--border-strong)]'}`}
                      style={sel ? { borderWidth: 2 } : {}}>
                      {o.l}
                    </button>
                  );
                })}
              </div>

              {isFail && (
                <div className="mt-3 rounded-[12px] p-3 flex gap-2" style={{ background: '#FFE9E0', border: '1px solid #EBC3AF' }}>
                  <p className="text-[12.5px] leading-snug" style={{ color: '#9A3200' }}>
                    <b>A fail raises a defect on {assetId2}</b> when you submit. Add a note and a photo so the workshop can act on it.
                  </p>
                </div>
              )}

              {showNote && (
                <div className="mt-3 space-y-2">
                  <input value={r.comment || ''} onChange={(e) => setComment(it.id, e.target.value)} placeholder={isFail ? 'What is wrong?' : 'Add a note'}
                    className="w-full h-11 rounded-[10px] bg-white border border-[var(--border-strong)] px-3 text-[16px] text-[var(--ink-primary)] outline-none focus:border-[#1A1A1A] focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]" data-testid={`insp-${idx}-note`} />
                  <div className="flex items-center gap-2">
                    {r.photo ? (
                      <div className="relative" style={{ width: 44, height: 44 }}>
                        <img src={r.photo} alt="" className="w-full h-full object-cover rounded-[8px]" />
                        <button onClick={() => setPhoto(it.id, null)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white flex items-center justify-center" style={{ border: '1px solid var(--border-strong)' }}><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <button onClick={() => photoInput(it.id)} className="flex items-center justify-center rounded-[8px]" style={{ width: 44, height: 44, border: '1.5px dashed var(--border-strong)' }}>
                        <Camera className="w-4 h-4 text-[var(--ink-tertiary)]" />
                      </button>
                    )}
                    <span className="text-[13px] text-[var(--ink-secondary)]">{r.photo ? '1 photo attached' : 'Add photo'}</span>
                    <input id={`photo-${it.id}`} type="file" accept="image/*" capture="environment" onChange={(e) => onPhoto(it.id, e)} className="hidden" />
                  </div>
                </div>
              )}

              {!showNote && (
                <button onClick={() => setExpanded((p) => ({ ...p, [it.id]: true }))}
                  className="w-full flex items-center justify-between mt-2 h-11" data-testid={`insp-${idx}-addnote`}>
                  <span className="text-[13px] font-semibold text-[var(--volt-ink)]">Add note or photo</span>
                  <span className="text-[12px] text-[var(--ink-tertiary)]">Optional</span>
                </button>
              )}
            </MobileCard>
          );
        })}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pt-2.5 pb-4 bg-[var(--surface-page)]" style={{ boxShadow: 'var(--elev-floating)' }}>
        {allAnswered ? (
          <button onClick={() => setFinish(true)} data-testid="insp-finish-btn"
            className="w-full rounded-[13px] bg-[var(--volt)] text-black flex items-center justify-center active:scale-[0.99] transition-transform"
            style={{ height: 56, fontWeight: 800, border: '1px solid var(--volt-edge)' }}>
            FINISH INSPECTION
          </button>
        ) : (
          <>
            <div className="w-full rounded-[13px] flex items-center justify-center" data-testid="insp-finish-blocked"
              style={{ height: 56, background: '#E6E2D9', border: '1px solid #D3CEC2', color: '#A29D93', fontWeight: 800 }}>
              FINISH INSPECTION
            </div>
            <p className="flex items-center justify-center gap-1.5 text-center text-[12.5px] mt-2" style={{ color: '#9A3200' }}>
              <span className="rounded-full" style={{ width: 6, height: 6, background: '#9A3200' }} />
              {total - answered} item{total - answered === 1 ? '' : 's'} left · next unanswered is {firstUnansweredIdx + 1}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default MobileInspectionFlow;
